import type { PictureFile, PictureFormat } from '../../../packages/pictures/PictureFile.js';
import {
    DEFAULT_WIDTHS,
    MAX_WIDTH,
    MIN_WIDTH,
    ORIGINAL_ROUTE_MAX_WIDTH,
    STANDARD_WIDTH,
} from '../../pictures/DEFAULT_WIDTHS.js';
import type { Picture } from '../../pictures/Picture.js';
import { buildPictureRoute } from '../../pictures/picture-route.js';

export interface PictureData {
    polishId: string;
    maxWidth: number;
    preRoute: string;
    ext: PictureFormat;
    version: string;
}

export interface PictureSourceSet {
    avif: string;
    webp: string;
    jpg: string; // Zmieniono z jpeg na jpg dla spójności
    source: string;
    srcsets: Record<string, string>;
    fallbackSrc: string;
}

export class PictureSrcHtml {
    private pictureSourceSet: PictureSourceSet | undefined;

    static toPictureData(pictureFile: PictureFile): PictureData {
        return {
            polishId: pictureFile.getPolishFileNameId(),
            maxWidth: pictureFile.width,
            preRoute: pictureFile.getPreRoute(),
            ext: pictureFile.ext,
            version: pictureFile.version,
        };
    }

    constructor(private picture: PictureData) {}

    public getThumbnailRoute(format: PictureFormat, width?: number): string {
        return buildPictureRoute(this.picture.preRoute, this.picture.polishId, format, this.picture.version, width);
    }

    /** Ten sam wariant co getThumbnailRoute, ale bez `?v=` (stały adres dla Google). */
    public getStableRoute(format: PictureFormat, width?: number): string {
        return buildPictureRoute(this.picture.preRoute, this.picture.polishId, format, null, width);
    }
    // ---------------- PRIVATE HELPERS ----------------

    /** * Tworzy pojedynczy deskryptor szerokości, np. "path/photo.300.webp 300w"
     */
    private buildWidthDescriptor(format: PictureFormat, width: number): string {
        // Sprawdzamy, czy to jest ten "graniczny" przypadek oryginału - trasa
        // bez szerokości daje natywny rozmiar tylko do ORIGINAL_ROUTE_MAX_WIDTH.
        const isOriginal = width === this.picture.maxWidth && width <= ORIGINAL_ROUTE_MAX_WIDTH;

        // Jeśli oryginał, wywołujemy route bez szerokości w nazwie pliku
        const route = isOriginal ? this.getThumbnailRoute(format) : this.getThumbnailRoute(format, width);

        return `${route} ${width}w`;
    }

    /**
     * Buduje pełny ciąg srcset dla konkretnego formatu
     */
    private buildSrcSetForFormat(fmt: PictureFormat, widths: number[]): string {
        if (!widths.length) return '';

        return widths.map((w) => this.buildWidthDescriptor(fmt, w)).join(', ');
    }

    private pickFallback(srcset: string): string {
        if (!srcset) return '';

        const variants = srcset
            .split(',')
            .map((s) => s.trim())
            .map((s) => {
                const [url, widthStr] = s.split(' ');
                return { url, width: parseInt(widthStr, 10) };
            })
            .filter((v) => !Number.isNaN(v.width));

        if (variants.length === 0) return '';

        // Unikamy window.innerWidth, by zachować spójność SSR/Hydration
        // Wybieramy najbliższą szerokość do STANDARD_WIDTH lub największą dostępną
        const targetWidth = STANDARD_WIDTH;
        const suitable = variants.find((v) => v.width >= targetWidth);

        return (suitable || variants[variants.length - 1]).url;
    }

    // ---------------- PUBLIC / UTILITY ----------------

    public getCannonicalRoute(picture: Picture): string {
        const width = PictureSrcHtml.getCanonicalTargetWidth(picture.pictureFile.width);
        return this.getThumbnailRoute(picture.pictureFile.ext, width);
    }

    // Obrazek deklarowany dla Google (sitemapa obrazów, schema.org
    // ImageObject, og:image): największy wariant, który serwer naprawdę
    // publikuje - oryginał do ORIGINAL_ROUTE_MAX_WIDTH, powyżej największa
    // szerokość z DEFAULT_WIDTHS (do MAX_WIDTH). Wcześniej źródła węższe niż
    // MAX_WIDTH były deklarowane jako 480 px, nawet gdy było z nich 1800 px.
    public getStableCannonicalRoute(picture: Picture): string {
        const width = PictureSrcHtml.getFullSizeRouteWidth(picture.pictureFile.width);
        return this.getStableRoute(picture.pictureFile.ext, width);
    }

    /** Szerokość w nazwie pliku deklarowanego wariantu; undefined = oryginał. */
    static getFullSizeRouteWidth(sourceWidth: number): number | undefined {
        if (sourceWidth > 0 && sourceWidth <= ORIGINAL_ROUTE_MAX_WIDTH) {
            return undefined;
        }
        return PictureSrcHtml.getFullSizeWidth(sourceWidth);
    }

    /** Rzeczywista szerokość deklarowanego wariantu (og:image:width, ImageObject.width). */
    static getFullSizeWidth(sourceWidth: number): number {
        if (!sourceWidth) {
            return MAX_WIDTH;
        }
        if (sourceWidth <= ORIGINAL_ROUTE_MAX_WIDTH) {
            return sourceWidth;
        }
        return Math.max(...DEFAULT_WIDTHS.filter((w): w is number => w != null && w <= Math.min(sourceWidth, MAX_WIDTH)));
    }

    static getCanonicalDimensions(sourceWidth: number, sourceHeight: number) {
        return PictureSrcHtml.scaleDimensions(sourceWidth, sourceHeight, PictureSrcHtml.getFullSizeWidth(sourceWidth));
    }

    // Lżejszy wariant do <img src> bez srcset (getCannonicalRoute): 480 px dla
    // źródeł węższych niż MAX_WIDTH, inaczej MAX_WIDTH.
    static getCanonicalTargetWidth(sourceWidth: number): number {
        return sourceWidth && sourceWidth < MAX_WIDTH ? MIN_WIDTH : MAX_WIDTH;
    }

    /** Wymiary wariantu z getCannonicalRoute (widoczne <img>), nie deklarowanego. */
    static getCanonicalRouteDimensions(sourceWidth: number, sourceHeight: number) {
        return PictureSrcHtml.scaleDimensions(
            sourceWidth,
            sourceHeight,
            Math.min(sourceWidth, PictureSrcHtml.getCanonicalTargetWidth(sourceWidth)),
        );
    }

    private static scaleDimensions(sourceWidth: number, sourceHeight: number, width: number) {
        return {
            width,
            height: sourceWidth > 0 ? Math.max(1, Math.round((sourceHeight * width) / sourceWidth)) : undefined,
        };
    }

    getSrcSet(): PictureSourceSet {
        if (this.pictureSourceSet) return this.pictureSourceSet;

        const ext = this.picture.ext;
        const uniqueFormats = [...new Set<PictureFormat>(['avif', 'webp', ext])];

        const optimalWidths = this.getSortedOptimalWidths();

        // 2. Generowanie wszystkich srcsetów za jednym zamachem
        const srcsets: Record<string, string> = {};
        uniqueFormats.forEach((fmt) => {
            srcsets[fmt] = this.buildSrcSetForFormat(fmt, optimalWidths);
        });

        // 3. Logika wyboru fallbacków (uproszczona)
        const jpgLike = srcsets.jpg || srcsets.png || '';
        const sourceSrcset = srcsets[ext] || jpgLike;

        // Zawsze preferujemy webp jako fallback dla src="...", jeśli dostępny
        const fallbackBaseSet = srcsets.webp || sourceSrcset;

        this.pictureSourceSet = {
            avif: srcsets.avif || '',
            webp: srcsets.webp || '',
            jpg: jpgLike,
            source: sourceSrcset,
            srcsets,
            fallbackSrc: this.pickFallback(fallbackBaseSet),
        };

        return this.pictureSourceSet;
    }

    private getSortedOptimalWidths(): number[] {
        const widths = DEFAULT_WIDTHS.filter((w): w is number => w != null && w <= this.picture.maxWidth);

        // Jeśli obrazek jest mniejszy niż najmniejszy DEFAULT_WIDTH, tablica
        // jest pusta, lub natywna szerokość mieści się między dwoma progami
        // (np. 400px trafia tylko próg 160, pomijając 480) — dokładamy
        // natywną szerokość (trasa oryginału), żeby nie serwować tylko
        // rozmytego 160w. Tylko do ORIGINAL_ROUTE_MAX_WIDTH: szerszy oryginał
        // serwer pomniejsza do tej szerokości, więc np. "2181w" dawało 1200 px.
        if (
            widths.length === 0 ||
            (widths[widths.length - 1] < this.picture.maxWidth && this.picture.maxWidth <= ORIGINAL_ROUTE_MAX_WIDTH)
        ) {
            widths.push(this.picture.maxWidth);
        }

        // Unikalne i posortowane
        return Array.from(new Set(widths)).sort((a, b) => a - b);
    }
}
